import React, { useEffect, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { TpmBar } from "../../home/chartKit";
import { HOME_SCALE } from "../../home/homeTheme";
import dayjs from "dayjs";
import "./VisitDataSummary.scss";
import Filters from "./Filters";
import { httpService } from "../../../../services/httpService.service";
import { Button } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { isAuthorized } from "../../../../services/authService";
import { RolesEnum } from "../../../../services/rolesEnum";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Accessibility = () => {
  const [distributionData, setDistributionData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const chartRef = useRef(null);
  const [filters, setFilters] = useState({
    country: "",
    state: "",
    startDate: "",
    endDate: "",
  });

  const fetchData = () => {
    const projectId = localStorage.getItem("currentProjectID");

    const startDate = localStorage.getItem("Contract-startDate");
    const endDate = localStorage.getItem("Contract-endDate");

    const params = {
      ProjectId: projectId,
      StartDate: filters.startDate || dayjs(startDate).startOf("month"),
      EndDate: filters.endDate || dayjs(endDate).endOf("month"),
      Country: filters.country,
      State: filters.state,
    };

    const queryString = new URLSearchParams(params).toString();

    httpService.get(
      `/api/Site/accessibilityInsights?${queryString}`,
      (res) => {
        setDistributionData(res?.data?.accessibilityDistribution);
        setCategoriesData(res?.data?.accesibilityCategoriesResponse);
      },
      (err) => {
        // console.log(err);
      }
    );
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const hasAccessibilityData =
    Array.isArray(distributionData) && distributionData.length > 0;

  const labels = hasAccessibilityData
    ? distributionData.map((item) => item.locationState)
    : [];

  const categories = hasAccessibilityData
    ? Object.keys(distributionData[0]).filter((key) => key !== "locationState")
    : [];

  const staticColors = {
    challenging: HOME_SCALE.poor,
    critical: HOME_SCALE.critical,
    semiAccessible: HOME_SCALE.fair,
    accessible: HOME_SCALE.good,
  };

  const generateRandomColor = () => {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  const accessibilityDistributionData = {
    labels,
    datasets: categories.map((category) => {
      const color = staticColors[category] || generateRandomColor();
      return {
        label: category.charAt(0).toUpperCase() + category.slice(1),
        data: hasAccessibilityData
          ? distributionData.map((item) => item[category] || 0)
          : Array(labels.length).fill(100),
        backgroundColor: hasAccessibilityData ? color : "rgba(0, 0, 0, 0.1)",
        borderColor: hasAccessibilityData ? color : "rgba(0, 0, 0, 0.1)",
        borderWidth: 1,
      };
    }),
  };

  const accessibilityDistributionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: true,
        text: hasAccessibilityData
          ? "Accessibility Distribution"
          : "No Data Available",
        font: {
          size: 18,
          weight: "600",
          style: "normal",
          family: "Poppins",
          lineHeight: "26px",
        },
        color: "#6A113C",
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return hasAccessibilityData
              ? `${tooltipItem.dataset.label}: ${tooltipItem.raw}`
              : "No Data Available";
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        min: 0,
        max: 100,
      },
      y: {
        stacked: true,
      },
    },
  };

  const hasData = categoriesData.some(
    (item) => item?.postponed > 0 || item?.completed > 0
  );

  const datasetKeys = ["postponed", "completed"];
  const colors = {
    postponed: HOME_SCALE.critical,
    completed: HOME_SCALE.good,
  };

  const statusOfVisitsData = {
    labels: categoriesData.map((item) => item?.optionName),
    datasets: hasData
      ? datasetKeys.map((key) => ({
          label: `Visits ${key.charAt(0).toUpperCase() + key.slice(1)}`,
          data: categoriesData.map((item) => item?.[key] || 0),
          backgroundColor: colors[key],
          borderColor: colors[key],
          borderWidth: 1,
        }))
      : [
          {
            label: "No data",
            data: Array(categoriesData.length).fill(100),
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            borderColor: "rgba(0, 0, 0, 0.1)",
            borderWidth: 1,
          },
        ],
  };

  const statusOfVisitsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: true,
        text: "Status of Visits Based on Accessibility Categories",
        font: {
          size: 18,
          weight: "600",
          style: "normal",
          family: "Poppins",
          lineHeight: "26px",
        },
        color: "#6A113C",
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            if (!hasData) return "No Data Available";
            return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        min: 0,
        max: 100,
      },
      y: {
        stacked: true,
      },
    },
  };

  const handleExportExcel = () => {
    const projectId = localStorage.getItem("currentProjectID");
    const params = {
      projectId: projectId,
      startDate: filters.startDate || dayjs().startOf("month"),
      endDate: filters.endDate || dayjs().endOf("month"),
      ...(filters.state ? { country: filters.state } : {}),
      ipAnalysis: false,
      exportFilters: {
        includeAccessibilityInsights: true,
      },
    };

    httpService.post(
      `/api/Site/exportVisitDataSummary`,
      params,
      (response) => {
        const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "Accessibility Insights.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      },
      (err) => {
        console.error(err);
      },
      { responseType: "arraybuffer" }
    );
  };

  const handleExportPDF = () => {
    if (!chartRef.current) return;

    toPng(chartRef.current, { cacheBust: true })
      .then((dataUrl) => {
        const pdf = new jsPDF();
        const imgWidth = 180; // Adjust the image width
        const imgHeight =
          (chartRef.current.offsetHeight / chartRef.current.offsetWidth) *
          imgWidth;

        pdf.addImage(dataUrl, "PNG", 15, 15, imgWidth, imgHeight);
        pdf.save("AccessibilityInsights.pdf");
      })
      .catch((err) => {
        console.error("Error exporting to PDF:", err);
      });
  };

  return (
    <>
      <div className="export-container  row gap-2">
        <div className="col-12 col-lg-8">
          <Filters filters={filters} setFilters={setFilters} />
        </div>
        {isAuthorized([
          RolesEnum.SystemAdmin,
          RolesEnum.ProjectManager,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldTeamLeader,
        ]) && (
          <div className="row gap-2 col-12 col-lg-4">
            <Button
              className="exportButtonTPM addButtonTPM col-12 col-md-5 col-lg-5"
              style={{
                padding: "5px 20px",
                fontSize: "13px",
                maxHeight: "35px",
              }}
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
            >
              Export to Excel
            </Button>
            <Button
              className="exportButtonTPM addButtonTPM col-12 col-md-5 col-lg-5"
              style={{
                padding: "5px 20px",
                fontSize: "13px",
                maxHeight: "35px",
                background: " var(--2, #6A113C) !important",
              }}
              icon={<DownloadOutlined />}
              onClick={handleExportPDF}
            >
              Export to PDF
            </Button>
          </div>
        )}
      </div>
      <div ref={chartRef} className="charts-container">
        <div className="chart-card" style={{ width: "100%" }}>
          <TpmBar
            data={accessibilityDistributionData}
            options={accessibilityDistributionOptions}
          />
        </div>
        <div className="chart-card" style={{ width: "100%", height: "400px" }}>
          <TpmBar data={statusOfVisitsData} options={statusOfVisitsOptions} />
        </div>
      </div>
    </>
  );
};

export default Accessibility;
